import { useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type RowSelectionState,
} from '@tanstack/react-table';
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Button } from '../ui/Button';

interface DataTableProps<TData> {
  data: TData[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[];
  enableRowSelection?: boolean;
  enablePagination?: boolean;
  pageSize?: number;
  onRowSelectionChange?: (selectedRows: TData[]) => void;
  onRowClick?: (row: TData) => void;
  isLoading?: boolean;
  emptyMessage?: string;
}

/**
 * Reusable data table component with sorting, selection, and pagination.
 * Optimized for admin data density.
 */
export function DataTable<TData>({
  data,
  columns,
  enableRowSelection = false,
  enablePagination = false,
  pageSize = 20,
  onRowSelectionChange,
  onRowClick,
  isLoading = false,
  emptyMessage = 'No data available',
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageSize,
      },
    },
    onSortingChange: setSorting,
    onRowSelectionChange: (updater) => {
      const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
      setRowSelection(newSelection);

      if (onRowSelectionChange) {
        const selectedRows = Object.keys(newSelection)
          .filter((key) => newSelection[key])
          .map((key) => data[parseInt(key)]);
        onRowSelectionChange(selectedRows);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    enableRowSelection,
  });

  const { pageIndex, pageSize: currentPageSize } = table.getState().pagination;
  const pageCount = table.getPageCount();

  return (
    <div className="w-full">
      <div className="overflow-auto custom-scrollbar">
        <table className="w-full border-collapse">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b border-border">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={cn(
                      'table-header-cell text-left',
                      'bg-surface-variant',
                      header.column.getCanSort() && 'cursor-pointer select-none hover:bg-surface-variant/80'
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="flex items-center gap-1.5">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <span className="text-text-muted">
                          {header.column.getIsSorted() === 'asc' ? (
                            <ChevronUp size={12} />
                          ) : header.column.getIsSorted() === 'desc' ? (
                            <ChevronDown size={12} />
                          ) : (
                            <ChevronsUpDown size={12} />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              // Loading skeleton rows
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="table-row">
                  {columns.map((_, j) => (
                    <td key={j} className="table-cell">
                      <div className="skeleton h-4" />
                    </td>
                  ))}
                </tr>
              ))
            ) : table.getRowModel().rows.length === 0 ? (
              // Empty state
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-table-cell py-8 text-center text-text-secondary text-body-sm"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              // Data rows
              table.getRowModel().rows.map((row, index) => (
                <tr
                  key={row.id}
                  className={cn(
                    'table-row',
                    // Subtle zebra striping
                    index % 2 === 1 && 'bg-surface-variant/30',
                    // Active/pressed state
                    onRowClick && 'cursor-pointer active:bg-surface-variant',
                    // Selection state (overrides zebra)
                    row.getIsSelected() && 'bg-action/10 hover:bg-action/15'
                  )}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="table-cell text-text">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {enablePagination && pageCount > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-divider">
          <div className="text-body-sm text-text-secondary">
            Showing {pageIndex * currentPageSize + 1} to{' '}
            {Math.min((pageIndex + 1) * currentPageSize, data.length)} of {data.length}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeft size={14} />
              Previous
            </Button>
            <span className="text-body-sm text-text-secondary">
              Page {pageIndex + 1} of {pageCount}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Selection checkbox column helper.
 */
export function createSelectionColumn<TData>(): ColumnDef<TData, unknown> {
  return {
    id: 'select',
    header: ({ table }) => (
      <input
        type="checkbox"
        checked={table.getIsAllRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
        className={cn(
          'h-4 w-4 rounded border-2 border-border',
          'text-action focus:ring-2 focus:ring-action focus:ring-offset-0',
          'transition-colors cursor-pointer',
          'checked:bg-action checked:border-action'
        )}
      />
    ),
    cell: ({ row }) => (
      <input
        type="checkbox"
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'h-4 w-4 rounded border-2 border-border',
          'text-action focus:ring-2 focus:ring-action focus:ring-offset-0',
          'transition-colors cursor-pointer',
          'checked:bg-action checked:border-action'
        )}
      />
    ),
    enableSorting: false,
  };
}
